import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContractContext } from "context/contract";
import { ClientContext } from "context/client";
import ClientService from "services/client.service";
import { ScheduleContext } from "context/schedule";
import { ProfileContext } from "context/profile";
import api from "services/api";
import HttpService from "services/htttp.service";
import InputMask from "react-input-mask";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import GlobalStyles from "@mui/material/GlobalStyles";

import { useMaterialUIController } from "context";
import Swal from "sweetalert2";

const brazilianStates = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", 
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export default function ContractForm() {
  const { eventId } = useParams();
  const parsedEventId = Number(eventId);
  const navigate = useNavigate();

  const {
    getByEvent,
    loadContractByEvent,
    createContract,
    updateContract,
    signContract,
  } = useContext(ContractContext);

  const { clients } = useContext(ClientContext);
  const { schedule } = useContext(ScheduleContext);
  const { user } = useContext(ProfileContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [controller] = useMaterialUIController();
  const { sidenavColor, darkMode } = controller;

  const [fetchedEvent, setFetchedEvent] = useState(null);
  const [fetchedClient, setFetchedClient] = useState(null);

  const contextEvent = schedule?.find((e) => Number(e.id) === parsedEventId);
  const event = contextEvent || fetchedEvent;

  const contextClient = clients?.find((c) => Number(c.id) === Number(event?.client_id));
  const client = contextClient || fetchedClient;

  useEffect(() => {
    if (!parsedEventId) return;
    if (!contextEvent && !fetchedEvent) {
      HttpService.get(`events/${parsedEventId}`)
        .then((response) => setFetchedEvent(response.data || response))
        .catch((err) => console.error("Erro ao buscar evento:", err));
    }
  }, [parsedEventId, contextEvent, fetchedEvent]);

  useEffect(() => {
    if (event?.client_id && !contextClient && !fetchedClient) {
      HttpService.get(`clients/${event.client_id}`)
        .then((response) => setFetchedClient(response.data || response))
        .catch((err) => console.error("Erro ao buscar cliente:", err));
    }
  }, [event, contextClient, fetchedClient]);

  /* =======================
     FORM (fonte da verdade)
  ======================= */
  const [contractId, setContractId] = useState(null);
  const [mode, setMode] = useState(null); // 'create' | 'update'
  const [updateClientProfile, setUpdateClientProfile] = useState(true);
  const [services, setServices] = useState([{ description: "" }]);

  const [form, setForm] = useState({
    status: "draft",

    total_value: "",
    signal_value: "",
    payment_method: "",
    notes: "",

    client_name: "",
    client_document: "",
    client_address: "",
    client_neighborhood: "",
    client_city: "",
    client_state: "",

    event_address: "",
    event_neighborhood: "",
    event_city: "",
    event_state: "",
    event_date: "",
    event_timeFrom: "",
    event_timeTo: "",

    provider_name: "",
    provider_document: "",
    provider_address: "",
    provider_neighborhood: "",
    provider_city: "",
    provider_state: "",
  });
  const [loading, setLoading] = useState(false);
  const [clientDocType, setClientDocType] = useState("PF");
  const [providerDocType, setProviderDocType] = useState("PF");

  const isSigned = form.status === "signed";

  /* =======================
     LOAD CONTRATO DO BACKEND
  ======================= */
  useEffect(() => {
    if (!parsedEventId) return;

    console.log('[ContractForm] Loading contract for eventId:', parsedEventId);

    loadContractByEvent(parsedEventId).then((contract) => {
      console.log('[ContractForm] Contract loaded:', contract);
      
      if (!contract) {
        // Nenhum contrato existe, entrar em modo CREATE
        console.log('[ContractForm] No contract found, setting mode to CREATE');
        setContractId(null);
        setMode('create');
        return;
      }

      // Contrato existe, entrar em modo UPDATE
      console.log('[ContractForm] Contract found, setting mode to UPDATE with ID:', contract.id);
      setContractId(contract.id);
      setMode('update');

      // Carregar serviços se existirem, senão inicia com um vazio
      if (contract.services && contract.services.length > 0) {
        setServices(contract.services.map(s => ({ description: s.description })));
      }

      // Definir tipos de documento baseados no valor carregado
      setClientDocType(contract.client_document && contract.client_document.replace(/\D/g, "").length > 11 ? "PJ" : "PF");
      setProviderDocType(contract.provider_document && contract.provider_document.replace(/\D/g, "").length > 11 ? "PJ" : "PF");

      setForm((prev) => ({
        ...prev,

        status: contract.status,

        total_value: contract.total_value ?? "",
        signal_value: contract.signal_value ?? "",
        payment_method: contract.payment_method ?? "",
        notes: contract.notes ?? "",

        client_name: contract.client_name ?? "",
        client_document: contract.client_document ?? "",
        client_address: contract.client_address ?? "",
        client_neighborhood: contract.client_neighborhood ?? "",
        client_city: contract.client_city ?? "",
        client_state: contract.client_state ?? "",

        event_address: contract.event_address ?? "",
        event_neighborhood: contract.event_neighborhood ?? "",
        event_city: contract.event_city ?? "",
        event_state: contract.event_state ?? "",
        event_date: contract.event_date ? contract.event_date.split("T")[0] : "",
        event_timeFrom: contract.event_timeFrom ?? "",
        event_timeTo: contract.event_timeTo ?? "",

        provider_name: contract.provider_name ?? "",
        provider_document: contract.provider_document ?? "",
        provider_address: contract.provider_address ?? "",
        provider_neighborhood: contract.provider_neighborhood ?? "",
        provider_city: contract.provider_city ?? "",
        provider_state: contract.provider_state ?? "",
      }));
    });
  }, [parsedEventId]);

  /* =======================
     PREFILL SISTEMA - apenas se campos estão vazios após load
  ======================= */
  useEffect(() => {
    // Só preencher se os campos principais estão vazios (não carregaram do banco)
    if (!event || !client || !user) return;

    setForm((prev) => {
      // Só preencher campos que estão realmente vazios
      const hasClientData = prev.client_name !== "" || prev.client_document !== "" || prev.client_address !== "";
      const hasEventData = prev.event_address !== "" || prev.event_date !== "";
      const hasProviderData = prev.provider_name !== "" || prev.provider_document !== "";

      // Se já tem dados do banco, não sobrescrever
      if (hasClientData && hasEventData && hasProviderData) {
        return prev;
      }

      // Definir tipos de documento baseados no preenchimento automático (apenas se estiver vazio)
      if (prev.client_document === "" && client.document) {
        setClientDocType(client.document.replace(/\D/g, "").length > 11 ? "PJ" : "PF");
      }
      if (prev.provider_document === "" && user.document) {
        setProviderDocType(user.document.replace(/\D/g, "").length > 11 ? "PJ" : "PF");
      }

      return {
        ...prev,

        // CLIENTE - preencher APENAS se vazio
        ...(prev.client_name === "" && { client_name: client.name }),
        ...(prev.client_document === "" && { client_document: client.document }),
        ...(prev.client_address === "" && { 
          client_address: (typeof client.address === 'object' ? client.address?.address : client.address) || ""
        }),
        ...(prev.client_neighborhood === "" && { 
          client_neighborhood: (typeof client.address === 'object' ? client.address?.neighborhood : client.neighborhood) || ""
        }),
        ...(prev.client_city === "" && { 
          client_city: (typeof client.address === 'object' ? client.address?.city : client.city) || ""
        }),
        ...(prev.client_state === "" && { 
          client_state: (typeof client.address === 'object' ? client.address?.state : client.state) || ""
        }),

        // EVENTO
        ...(prev.event_address === "" && { event_address: event.location || event.address?.address || "" }),
        ...(prev.event_neighborhood === "" && { event_neighborhood: event.address?.neighborhood || "" }),
        ...(prev.event_city === "" && { event_city: event.city || event.address?.city || "" }),
        ...(prev.event_state === "" && { event_state: event.address?.state || "" }),
        ...(prev.event_date === "" && { event_date: event.event_date ? event.event_date.split("T")[0] : "" }),
        ...(prev.event_timeFrom === "" && { event_timeFrom: event.start_time || "" }),
        ...(prev.event_timeTo === "" && { event_timeTo: event.end_time || "" }),
        ...(prev.total_value === "" && { total_value: event.price || "" }),

        // CONTRATADO (PROVEDOR)
        ...(prev.provider_name === "" && { 
          provider_name: user.artist_name || `${user.name} ${user.last_name}`.trim()
        }),
        ...(prev.provider_document === "" && { provider_document: user.document || "" }),
        ...(prev.provider_address === "" && { 
          provider_address: (typeof user.address === 'object' ? user.address?.address : user.address) || ""
        }),
        ...(prev.provider_neighborhood === "" && { 
          provider_neighborhood: (typeof user.address === 'object' ? user.address?.neighborhood : user.neighborhood) || ""
        }),
        ...(prev.provider_city === "" && { 
          provider_city: (typeof user.address === 'object' ? user.address?.city : user.city) || ""
        }),
        ...(prev.provider_state === "" && { 
          provider_state: (typeof user.address === 'object' ? user.address?.state : user.state) || ""
        }),
      };
    });
  }, [event, client, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        name === "total_value" || name === "signal_value"
          ? Number(value)
          : value,
    }));
  };

  // Manipulação dos Serviços
  const handleServiceChange = (index, value) => {
    const newServices = [...services];
    newServices[index].description = value;
    setServices(newServices);
  };

  const addService = () => {
    setServices([...services, { description: "" }]);
  };

  const removeService = (index) => {
    const newServices = services.filter((_, i) => i !== index);
    setServices(newServices);
  };

  /* =======================
     SALVAR
  ======================= */
  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('=== SAVE START ===');
      console.log('Mode:', mode);
      console.log('ContractId:', contractId);
      console.log('Event:', event);
      console.log('Client:', client);

      // Validações básicas
      if (!event || !client) {
        Swal.fire("Erro", "Evento ou cliente não encontrado", "error");
        return;
      }

      if (mode !== 'create' && mode !== 'update') {
        Swal.fire("Erro", "Estado do contrato indeterminado (mode inválido)", "error");
        return;
      }

      // Validação de campos obrigatórios
      const requiredFields = [
        { value: form.client_name, label: "Nome do Cliente" },
        { value: form.client_document, label: "Documento do Cliente" },
        { value: form.client_address, label: "Endereço do Cliente" },
        { value: form.client_neighborhood, label: "Bairro do Cliente" },
        { value: form.client_city, label: "Cidade do Cliente" },
        { value: form.client_state, label: "Estado do Cliente" },
        { value: form.event_address, label: "Endereço do Evento" },
        { value: form.event_neighborhood, label: "Bairro do Evento" },
        { value: form.event_city, label: "Cidade do Evento" },
        { value: form.event_state, label: "Estado do Evento" },
        { value: form.event_date, label: "Data do Evento" },
        { value: form.event_timeFrom, label: "Horário de Início" },
        { value: form.event_timeTo, label: "Horário de Término" },
        { value: form.provider_name, label: "Nome do Contratado" },
        { value: form.provider_document, label: "Documento do Contratado" },
        { value: form.provider_address, label: "Endereço do Contratado" },
        { value: form.provider_neighborhood, label: "Bairro do Contratado" },
        { value: form.provider_city, label: "Cidade do Contratado" },
        { value: form.provider_state, label: "Estado do Contratado" },
        { value: form.total_value, label: "Valor Total" },
        { value: form.payment_method, label: "Forma de Pagamento" },
      ];

      const missingFields = requiredFields.filter(f => !f.value || (typeof f.value === 'string' && f.value.trim() === "") || (typeof f.value === 'number' && f.value <= 0));

      if (missingFields.length > 0) {
        const missingList = missingFields.map(f => `<li>${f.label}</li>`).join("");
        Swal.fire({
          icon: "warning",
          title: "Campos Obrigatórios",
          html: `Por favor, preencha os seguintes campos:<br/><ul style="text-align: left; margin-top: 10px; margin-left: 20px;">${missingList}</ul>`,
        });
        setLoading(false);
        return;
      }

      // Preparar payload (sem incluir event_id e client_id nele, pois vão na raiz)
      const payload = {
        total_value: form.total_value,
        signal_value: form.signal_value,
        payment_method: form.payment_method,
        notes: form.notes,
        status: form.status,
        client_name: form.client_name,
        client_document: form.client_document,
        client_address: form.client_address,
        client_neighborhood: form.client_neighborhood,
        client_city: form.client_city,
        client_state: form.client_state,
        event_address: form.event_address,
        event_neighborhood: form.event_neighborhood,
        event_city: form.event_city,
        event_state: form.event_state,
        event_date: form.event_date,
        event_timeFrom: form.event_timeFrom,
        event_timeTo: form.event_timeTo,
        provider_name: form.provider_name,
        provider_document: form.provider_document,
        provider_address: form.provider_address,
        provider_neighborhood: form.provider_neighborhood,
        provider_city: form.provider_city,
        provider_state: form.provider_state,
        services: services.filter(s => s.description.trim() !== ""), // Envia apenas preenchidos
      };

      console.log('Payload:', payload);

      let clientUpdateSuccess = false;

      // ATUALIZAR PERFIL DO CLIENTE (Enriquecimento Progressivo)
      if (updateClientProfile && client) {
        try {
          const clientPayload = {
            name: form.client_name,
            document: form.client_document,
            address: form.client_address,
            neighborhood: form.client_neighborhood,
            city: form.client_city,
            state: form.client_state,
            country: "Brasil",
            postal_code: "00000-000",
          };
          await ClientService.updateClient(client.id, clientPayload);
          clientUpdateSuccess = true;
          console.log('Client profile updated successfully');
        } catch (err) {
          console.warn('Failed to update client profile:', err);
          Swal.fire({
            icon: "warning",
            title: "Atenção",
            text: "O contrato foi salvo, mas houve um erro ao atualizar o cadastro do cliente.",
          });
          // Não bloqueia o salvamento do contrato, apenas avisa no console
        }
      }

      // ATUALIZAR DADOS DO EVENTO (Sincronizar com o contrato)
      if (event) {
        try {
          const eventPayload = {
            event_date: form.event_date,
            start_time: form.event_timeFrom,
            end_time: form.event_timeTo,
            location: form.event_address,
            city: form.event_city,
            price: form.total_value,
          };
          await HttpService.patch(`events/${event.id}`, eventPayload);
          console.log('Event data updated successfully to match contract');
        } catch (err) {
          console.warn('Failed to sync event data:', err);
        }
      }

      // UPDATE - contrato já existe
      if (mode === 'update' && contractId) {
        console.log('Updating contract with ID:', contractId);
        await updateContract(contractId, payload);
        Swal.fire(
          "Sucesso!",
          `Contrato atualizado${clientUpdateSuccess ? " e cadastro do cliente salvo" : ""}.`,
          "success"
        );
        return;
      }

      // CREATE - novo contrato
      if (mode === 'create') {
        const createPayload = {
          event_id: event.id,
          client_id: client.id,
          ...payload,
        };
        console.log('Creating contract with payload:', createPayload);
        const newContract = await createContract(createPayload);
        console.log('New contract created:', newContract);
        setContractId(newContract.id);
        setMode('update');
        Swal.fire({
          title: "Sucesso!",
          text: `Contrato criado${clientUpdateSuccess ? " e cadastro do cliente salvo" : ""}.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        }).then(() => navigate("/events"));
        return;
      }
    } catch (e) {
      console.error('=== ERRO AO SALVAR ===');
      console.error('Error object:', e);
      console.error('Error message:', e.message);
      console.error('Error response:', e.response?.data);
      Swal.fire("Erro", e.response?.data?.message || "Não foi possível salvar o contrato", "error");
    } finally {
      setLoading(false);
    }
  };


  /* =======================
     ASSINAR
  ======================= */
  const handleSign = async () => {
    if (isSigned) return;

    const contract = getByEvent(parsedEventId);
    if (!contract) return;

    const result = await Swal.fire({
      title: "Assinar contrato?",
      text: "A assinatura apenas marca o contrato como assinado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Assinar",
    });

    if (!result.isConfirmed) return;

    try {
      await signContract(contract.id);
      setForm((prev) => ({ ...prev, status: "signed" }));
      Swal.fire("Contrato assinado", "", "success");
    } catch (error) {
      console.error("Erro ao assinar contrato:", error);
      Swal.fire("Erro", "Não foi possível assinar o contrato.", "error");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrato_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Não foi possível baixar o PDF", "error");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (!event || !client) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={{ xs: 0.5, md: 3 }}>
          <MDTypography>Carregando contrato...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <GlobalStyles styles={{ ".swal2-container": { zIndex: "99999 !important" } }} />
      <DashboardNavbar />

      <MDBox p={{ xs: 0.5, md: 3 }} maxWidth="900px">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h4">
            Contrato do Evento
          </MDTypography>
          {contractId && (
            <MDButton variant="gradient" color="error" onClick={handleDownloadPdf}>
              Baixar PDF
            </MDButton>
          )}
        </MDBox>

        <MDBadge
          badgeContent={form.status.toUpperCase()}
          color={isSigned ? "success" : "warning"}
          variant="gradient"
        />

        {/* CLIENTE */}
        <MDBox mt={4}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography fontWeight="bold">Cliente</MDTypography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateClientProfile}
                  onChange={(e) => setUpdateClientProfile(e.target.checked)}
                  color="primary"
                />
              }
              label={<MDTypography variant="caption">Salvar dados no cadastro do cliente</MDTypography>}
            />
          </MDBox>
          <MDInput label="Nome" name="client_name" value={form.client_name} onChange={handleChange} fullWidth />
          <MDBox mt={2} display="flex" gap={2}>
            <MDBox width="120px">
              <MDInput
                select
                label="Tipo"
                value={clientDocType}
                onChange={(e) => setClientDocType(e.target.value)}
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="PF" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>CPF</option>
                <option value="PJ" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>CNPJ</option>
              </MDInput>
            </MDBox>
            <MDBox flex={1}>
              <InputMask
                mask={clientDocType === "PJ" ? "99.999.999/9999-99" : "999.999.999-99"}
                value={form.client_document}
                onChange={handleChange}
                disabled={false}
              >
                {(inputProps) => (
                  <MDInput {...inputProps} label="Documento" name="client_document" fullWidth />
                )}
              </InputMask>
            </MDBox>
          </MDBox>
          <MDInput label="Endereço" name="client_address" value={form.client_address} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDInput label="Bairro" name="client_neighborhood" value={form.client_neighborhood} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDBox display="flex" gap={2} mt={2}>
            <MDInput label="Cidade" name="client_city" value={form.client_city} onChange={handleChange} fullWidth />
            <MDInput select label="Estado" name="client_state" value={form.client_state} onChange={handleChange} SelectProps={{ native: true }} fullWidth>
              <option value="" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
                Selecione
              </option>
              {brazilianStates.map((uf) => (
                <option key={uf} value={uf} style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>{uf}</option>
              ))}
            </MDInput>
          </MDBox>
        </MDBox>

        {/* EVENTO */}
        <MDBox mt={4}>
          <MDTypography fontWeight="bold">Evento</MDTypography>
          <MDInput label="Endereço" name="event_address" value={form.event_address} onChange={handleChange} fullWidth />
          <MDInput label="Bairro" name="event_neighborhood" value={form.event_neighborhood} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDBox display="flex" gap={2} mt={2}>
            <MDInput label="Cidade" name="event_city" value={form.event_city} onChange={handleChange} fullWidth />
            <MDInput select label="Estado" name="event_state" value={form.event_state} onChange={handleChange} SelectProps={{ native: true }} fullWidth>
              <option value="" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
                Selecione
              </option>
              {brazilianStates.map((uf) => (
                <option key={uf} value={uf} style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>{uf}</option>
              ))}
            </MDInput>
          </MDBox>
          <MDBox display="flex" gap={2} mt={2}>
            <MDInput 
              type="date" 
              label="Data" 
              name="event_date" 
              value={form.event_date} 
              onChange={handleChange} 
              fullWidth 
              InputLabelProps={{ shrink: true }}
            />
            <MDInput label="Início" name="event_timeFrom" value={form.event_timeFrom} onChange={handleChange} fullWidth />
            <MDInput label="Fim" name="event_timeTo" value={form.event_timeTo} onChange={handleChange} fullWidth />
          </MDBox>
        </MDBox>

        {/* CONTRATADO */}
        <MDBox mt={4}>
          <MDTypography fontWeight="bold">Contratado</MDTypography>
          <MDInput label="Nome" name="provider_name" value={form.provider_name} onChange={handleChange} fullWidth />
          <MDBox mt={2} display="flex" gap={2}>
            <MDBox width="120px">
              <MDInput
                select
                label="Tipo"
                value={providerDocType}
                onChange={(e) => setProviderDocType(e.target.value)}
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="PF" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>CPF</option>
                <option value="PJ" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>CNPJ</option>
              </MDInput>
            </MDBox>
            <MDBox flex={1}>
              <InputMask
                mask={providerDocType === "PJ" ? "99.999.999/9999-99" : "999.999.999-99"}
                value={form.provider_document}
                onChange={handleChange}
                disabled={false}
              >
                {(inputProps) => (
                  <MDInput {...inputProps} label="Documento" name="provider_document" fullWidth />
                )}
              </InputMask>
            </MDBox>
          </MDBox>
          <MDInput label="Endereço" name="provider_address" value={form.provider_address} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDInput label="Bairro" name="provider_neighborhood" value={form.provider_neighborhood} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDBox display="flex" gap={2} mt={2}>
            <MDInput label="Cidade" name="provider_city" value={form.provider_city} onChange={handleChange} fullWidth />
            <MDInput select label="Estado" name="provider_state" value={form.provider_state} onChange={handleChange} SelectProps={{ native: true }} fullWidth>
              <option value="" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
                Selecione
              </option>
              {brazilianStates.map((uf) => (
                <option key={uf} value={uf} style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>{uf}</option>
              ))}
            </MDInput>
          </MDBox>
        </MDBox>

        {/* SERVIÇOS */}
        <MDBox mt={4}>
          <MDTypography fontWeight="bold" mb={2}>Serviços Contratados</MDTypography>
          {services.map((service, index) => (
            <MDBox key={index} display="flex" gap={1} mb={2} alignItems="center">
              <MDInput
                label={`Descrição do Serviço ${index + 1}`}
                value={service.description}
                onChange={(e) => handleServiceChange(index, e.target.value)}
                fullWidth
                multiline
              />
              <IconButton onClick={() => removeService(index)} color="error" disabled={services.length === 1 && index === 0}>
                <Icon>delete</Icon>
              </IconButton>
            </MDBox>
          ))}
          
          <MDButton
            size="small"
            variant="outlined"
            color={sidenavColor}
            onClick={addService}
            startIcon={<Icon>add</Icon>}
          >
            Adicionar Item
          </MDButton>
        </MDBox>

        {/* PAGAMENTO */}
        <MDBox mt={4}>
          <MDTypography fontWeight="bold">Pagamento</MDTypography>
          <MDInput label="Valor total" name="total_value" value={form.total_value} onChange={handleChange} fullWidth />
          <MDInput label="Sinal" name="signal_value" value={form.signal_value} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
          <MDInput 
            select 
            label="Forma de pagamento" 
            name="payment_method" 
            value={form.payment_method} 
            onChange={handleChange} 
            fullWidth 
            sx={{ mt: 2 }}
            SelectProps={{ native: true }}
          >
            <option value="" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>Selecione</option>
            <option value={`Entrada de ${formatCurrency(form.signal_value)} + Restante até a data do evento.`} style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
              Entrada de {formatCurrency(form.signal_value)} + Restante até a data do evento.
            </option>
            <option value="A vista na assinatura do contrato." style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
              A vista na assinatura do contrato.
            </option>
            <option value="A vista a pagar até a data do evento" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
              A vista a pagar até a data do evento
            </option>
            <option value="Crédito a vista" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
              Crédito a vista
            </option>
            <option value="Crédito parcelado" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#1a2035" : "#fff" }}>
              Crédito parcelado
            </option>
          </MDInput>
          <MDInput label="Observações" name="notes" value={form.notes} onChange={handleChange} multiline rows={3} fullWidth sx={{ mt: 2 }} />
        </MDBox>

        <MDBox mt={4}>
          <MDButton color={sidenavColor} onClick={handleSave} disabled={loading}>
            {loading ? "Salvando..." : "Salvar"}
          </MDButton>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
